import React, { useEffect, useState } from 'react';
import { Form, Input, message, Spin, Card, Alert, Breadcrumb, Tooltip } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchSqlData, updateData } from '../../../services/api';
import type { VwTkfWbsHierarchy, PrimaveraTaskSmall } from '../../../types';
import Button from '../../../components/common/Button';

const ToPlnProjSystemsEdit: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams(); // 'id' now matches the route param
  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch the project system by id
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['to_pln_proj_systems', id],
    queryFn: async () => {
      if (!id) return null;
      const res = await fetchSqlData('to_pln_proj_systems', 'sys_id', id);
      return res && res.length > 0 ? res[0] : null;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (data && data.sys_id) {
      form.setFieldsValue({
        system_id: data.sys_id,
        sys_name: data.sys_name,
      });
    }
  }, [data, form]);

  const handleFinish = async (values: any) => {
    if (!id) {
      message.error('System ID is missing.');
      return;
    }
    setIsUpdating(true);
    try {
      await updateData('to_pln_proj_systems', id, { sys_name: values.sys_name }, 'sys_id');
      message.success('System updated successfully!');
      navigate(-1);
    } catch (err: any) {
      message.error('Update failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) return <Spin />;
  if (isError) return <Alert type="error" message="Error loading system" description={error?.message || 'Unknown error'} />;
  if (!data) return <Alert type="warning" message="System not found" />;

  return (
    <Card variant="outlined" title={`Edit Project System: ${id}`} style={{ maxWidth: 500, margin: '2rem auto' }}>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item label="System ID" name="system_id" rules={[{ required: true }]}>
          <Input disabled />
        </Form.Item>
        <Form.Item label="System Name" name="sys_name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item>
          <Button action="edit" type="primary" htmlType="submit" loading={isUpdating}>
            Save
          </Button>
          <Button style={{ marginLeft: 8 }} onClick={() => navigate(-1)}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default ToPlnProjSystemsEdit;
